import React from 'react';
import {
  Amount,
  Box,
  Button,
  PlusIcon,
  Table,
  TableBody,
  TableFooter,
  TableFooterCell,
  TableFooterRow,
  TableHeader,
  TableHeaderCell,
  TableHeaderRow,
  Tooltip,
  TooltipInteractiveWrapper,
} from '@razorpay/blade/components';
import { OTEntryRow } from './OTEntryRow';
import type { LocalEntry, OtFormulaCardWithLabel } from './types';
import { ResetLocalOverridingStyles } from 'components/RunPayroll/ComplianceActions/styles';
import { computeRowDisplayedAmount } from './utils';

type OvertimeTableProps = {
  entries: LocalEntry[];
  formulas: OtFormulaCardWithLabel[];
  onAdd: () => void;
  onUpdate: (id: string, patch: Partial<LocalEntry>) => void;
  onRemove: (id: string) => void;
  onCalculate: (entryId: string) => void;
  calculatingEntryId: string | null;
};

export const OvertimeTable = ({
  entries,
  formulas,
  onAdd,
  onUpdate,
  onRemove,
  onCalculate,
  calculatingEntryId,
}: OvertimeTableProps) => {
  const usedFormulaIds = new Set(
    entries.map((entry) => entry.otFormulaCardId).filter((id): id is string => Boolean(id)),
  );

  const allFormulasUsed =
    entries.filter((entry) => entry.otFormulaCardId).length >= formulas.length;

  const total = entries.reduce((sum, entry) => sum + computeRowDisplayedAmount(entry), 0);

  const showTotal = entries.length > 1;

  const tableData = {
    nodes: entries.map((entry) => ({ ...entry, id: entry.id })),
  };

  return (
    <ResetLocalOverridingStyles>
      <Table
        rowDensity="comfortable"
        data={tableData}
        gridTemplateColumns="30% 2fr 2fr 1fr"
        showBorderedCells>
        {(tableData) => (
          <>
            <TableHeader>
              <TableHeaderRow>
                <TableHeaderCell>Formula</TableHeaderCell>
                <TableHeaderCell>Overtime Hours</TableHeaderCell>
                <TableHeaderCell>Amount</TableHeaderCell>
                <TableHeaderCell>{''}</TableHeaderCell>
              </TableHeaderRow>
            </TableHeader>

            <TableBody>
              {tableData.map((entry, idx) => (
                <OTEntryRow
                  key={entry.id}
                  row={entry}
                  rowIndex={idx}
                  formulas={formulas}
                  usedFormulaIds={
                    new Set([...usedFormulaIds].filter((id) => id !== entry.otFormulaCardId))
                  }
                  onUpdate={(patch) => onUpdate(entry.id, patch)}
                  onRemove={() => onRemove(entry.id)}
                  onCalculate={() => onCalculate(entry.id)}
                  isCalculating={calculatingEntryId === entry.id}
                />
              ))}
            </TableBody>

            {showTotal && (
              <TableFooter>
                <TableFooterRow>
                  <TableFooterCell>{''}</TableFooterCell>
                  <TableFooterCell textAlign="right">Total</TableFooterCell>
                  <TableFooterCell>
                    <Amount value={total} currency="INR" suffix="none" />
                  </TableFooterCell>
                  <TableFooterCell> {''}</TableFooterCell>
                </TableFooterRow>
              </TableFooter>
            )}
          </>
        )}
      </Table>

      <Box marginTop="spacing.4">
        {allFormulasUsed ? (
          <Tooltip content="All available formulas are already added" placement="top">
            <TooltipInteractiveWrapper>
              <Button variant="tertiary" size="small" icon={PlusIcon} isDisabled>
                New overtime entry
              </Button>
            </TooltipInteractiveWrapper>
          </Tooltip>
        ) : (
          <Button variant="tertiary" size="small" icon={PlusIcon} onClick={onAdd}>
            New overtime entry
          </Button>
        )}
      </Box>
    </ResetLocalOverridingStyles>
  );
};
